import { createContext, useContext, useEffect, type RefObject } from "react";

/**
 * Shared ref to the wizard's scroll container.
 * Provided by HelpCenterModal, consumed by step components.
 */
export const ScrollContainerContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

/**
 * Scroll-driven spotlight effect.
 *
 * Dims the element based on its distance from the viewport center.
 * Steps near the center stay bright (opacity 1); steps far away fade
 * to ~0.2 opacity. Direct DOM manipulation (no re-renders on scroll).
 *
 * If the content fits without scrolling, everything stays at full opacity.
 */
export function useScrollSpotlight(elementRef: RefObject<HTMLDivElement | null>) {
  const containerRef = useContext(ScrollContainerContext);

  useEffect(() => {
    const el = elementRef.current;
    const container = containerRef?.current;
    if (!el || !container) return;

    let rafId: number;

    const update = () => {
      // If everything fits on screen, no spotlight needed
      if (container.scrollHeight <= container.clientHeight + 40) {
        el.style.opacity = "1";
        return;
      }

      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();

      // Narrow container (docked panel) → gentler spotlight
      const isDocked = cRect.width < 500;

      // Focus point — slightly above center (where eyes naturally rest)
      const focusY = cRect.top + cRect.height * 0.4;
      // For tall elements, anchor to the top portion
      const anchorY = eRect.top + Math.min(eRect.height, 180) / 2;

      const distance = Math.abs(anchorY - focusY);

      // In docked mode, wider bright zone and higher minimum opacity
      const fadeStart = cRect.height * (isDocked ? 0.35 : 0.35);
      const fadeEnd = cRect.height * (isDocked ? 0.75 : 0.75);
      const minOpacity = isDocked ? 0.45 : 0.35;

      let opacity: number;
      if (distance <= fadeStart) {
        opacity = 1;
      } else if (distance >= fadeEnd) {
        opacity = minOpacity;
      } else {
        const t = (distance - fadeStart) / (fadeEnd - fadeStart);
        opacity = 1 - t * t * (1 - minOpacity);
      }

      el.style.opacity = String(Math.round(opacity * 100) / 100);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    container.addEventListener("scroll", onScroll, { passive: true });

    // Initial update after layout settles
    rafId = requestAnimationFrame(() => requestAnimationFrame(update));

    return () => {
      container.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
      el.style.opacity = "1";
    };
  }, [elementRef, containerRef]);
}
